import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, ArrowLeft, Loader2, CheckCircle2, AlertCircle, Smartphone } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { APP_NAME, IS_MAGIC_TEAM } from '../lib/brand';
import { isStandalonePwa } from '../lib/isStandalonePwa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token'), [searchParams]);
  const inPwa = useMemo(() => isStandalonePwa(), []);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!success || !inPwa) return undefined;
    const timer = setTimeout(() => navigate('/login'), 3000);
    return () => clearTimeout(timer);
  }, [success, inPwa, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Le password non coincidono');
      return;
    }
    if (password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il reset');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-white">
        <div className="max-w-md w-full text-center">
          <BrandLogo size={96} radius={16} className={`mx-auto mb-6${IS_MAGIC_TEAM ? '' : ' shadow-xl'}`} />
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-medium text-zinc-900 mb-2">Link non valido</h1>
          <p className="text-zinc-600 text-sm mb-6">Il link di reset non contiene un token valido.</p>
          <Link to="/forgot-password">
            <Button className="bg-[#D4AF37] hover:bg-[#B8941F] text-white rounded-full">
              Richiedi un nuovo link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FAFAFA] to-[#F4F4F5] pointer-events-none" />
      <div className="w-full max-w-md relative animate-fade-in">
        <div className="text-center mb-8">
          <BrandLogo size={96} radius={16} className={`mx-auto mb-4${IS_MAGIC_TEAM ? '' : ' shadow-xl'}`} />
          <h1 className="text-2xl font-light tracking-tight text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Nuova <span className="text-gold-gradient font-medium">Password</span>
          </h1>
        </div>

        {!inPwa && !success && (
          <div className="mb-4 p-3 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/25 text-sm text-zinc-700 leading-relaxed">
            Stai completando il reset dal link nell&apos;email. Dopo aver salvato la password,
            <strong> chiudi questa pagina del browser </strong>
            e accedi dall&apos;app <strong>{APP_NAME}</strong> installata sul telefono.
          </div>
        )}

        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-xl">
          {success ? (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-lg font-medium text-zinc-900 mb-2">Password aggiornata!</h2>
              {inPwa ? (
                <>
                  <p className="text-zinc-600 text-sm mb-4">Ora puoi accedere con la tua nuova password.</p>
                  <p className="text-zinc-500 text-xs">Ti stiamo reindirizzando al login...</p>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <p className="text-zinc-600 text-sm mb-3 leading-relaxed">
                    Operazione completata. <strong>Chiudi questa finestra del browser</strong> (torna alla mail o alla home del telefono).
                  </p>
                  <p className="text-zinc-600 text-sm mb-4 leading-relaxed">
                    Apri l&apos;app <strong>{APP_NAME}</strong> dall&apos;icona installata e accedi con la nuova password.
                  </p>
                  <p className="text-zinc-500 text-xs leading-relaxed">
                    Non continuare a usare questa pagina web: eviti sessioni duplicate e confusione tra browser e app.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              <p className="text-zinc-600 text-sm mb-6">Scegli una nuova password sicura per il tuo account.</p>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-700">Nuova password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input type="password" placeholder="Almeno 6 caratteri"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-white border-zinc-200" required minLength={6}
                      data-testid="reset-password-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-700">Conferma password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input type="password" placeholder="Ripeti password"
                      value={confirm} onChange={(e) => setConfirm(e.target.value)}
                      className="pl-10 bg-white border-zinc-200" required minLength={6}
                      data-testid="reset-confirm-input" />
                  </div>
                </div>
                <Button type="submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-white font-semibold rounded-full py-5"
                  disabled={loading}
                  data-testid="reset-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiorna password'}
                </Button>
              </form>
              {inPwa && (
                <div className="mt-6 text-center">
                  <Link to="/login" className="text-zinc-500 hover:text-zinc-700 text-sm inline-flex items-center gap-1">
                    <ArrowLeft className="w-4 h-4" /> Torna al login
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
